/**
 * BusinessRedirectRibbon (iter-115)
 *
 * Slim top strip surfaced on the public marketing surfaces (Landing + Login)
 * that nudges business visitors — who landed on the personal becaim.app by
 * mistake — over to https://business.becaim.app.
 *
 * Placement rationale: shown ONLY on Landing + Login (the public surfaces
 * a business owner is most likely to land on). Not mounted on the
 * authenticated Dashboard because logged-in personal users aren't the
 * target audience.
 *
 * Dismiss: stored per-device in localStorage for 30 days. Business visitors
 * who tap through won't see it again on this browser.
 *
 * Styling: dark navy (contrasting the purple personal-brand hue) so it
 * reads as a clearly-different product tier at a glance.
 */
import { useState, useEffect } from 'react';
import { X, Briefcase, ArrowRight } from 'lucide-react';

const DISMISS_KEY = 'gm_biz_ribbon_dismissed_at';
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const BUSINESS_URL = 'https://business.becaim.app';

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

export default function BusinessRedirectRibbon() {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isRecentlyDismissed()) setDismissed(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      className="w-full bg-gradient-to-r from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white"
      data-testid="business-redirect-ribbon"
      role="region"
      aria-label="Looking for BecAim for Business"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2.5">
        <div className="w-8 h-8 rounded-lg bg-[#FBBF24] text-[#0F172A] flex items-center justify-center shrink-0">
          <Briefcase size={16} strokeWidth={3} />
        </div>
        <p className="text-xs sm:text-sm font-bold leading-snug min-w-0 flex-1">
          <span className="hidden sm:inline">Looking for </span>
          <span className="text-[#FBBF24] font-extrabold">Business BecAim</span>
          <span className="hidden sm:inline"> to track your team&apos;s goals, missions and performance?</span>
          <span className="sm:hidden"> for teams?</span>
        </p>
        <a
          href={BUSINESS_URL}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1 bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] text-xs font-extrabold px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors"
          data-testid="business-redirect-cta"
        >
          Go there
          <ArrowRight size={13} strokeWidth={3} />
        </a>
        <button
          onClick={dismiss}
          className="p-1 text-white/70 hover:text-white hover:bg-white/10 rounded-md shrink-0"
          aria-label="Dismiss"
          data-testid="business-redirect-dismiss"
        >
          <X size={16} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
